import { Card, CardContent, CardHeader, CardTitle } from "../../../client/components/ui/card";
import useTranslation from "../../../client/hooks/useTranslation";
import type { ReportMetrics } from "../../metrics/types";
import { formatCurrency } from "./formatValue";

/** Synthèse des payouts de la période : total, nombre, meilleur payout et
 *  répartition par prop firm. Retourne null si le snapshot est en v1. */
export function PayoutsSection({ metrics }: { metrics: ReportMetrics }) {
  const t = useTranslation();
  const payouts = metrics.payoutsSummary;
  if (!payouts) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t.reports.payoutsTitle}</CardTitle>
      </CardHeader>
      <CardContent>
        {payouts.count === 0 ? (
          <div className="text-sm text-muted-foreground">{t.reports.payoutsNone}</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-xs text-muted-foreground">{t.reports.payoutsTotal}</div>
                <div className="text-xl font-mono font-semibold text-emerald-500">
                  {formatCurrency(payouts.total, metrics.currency)}
                </div>
              </div>

              <div>
                <div className="text-xs text-muted-foreground">{t.reports.payoutsCount}</div>
                <div className="text-xl font-mono font-semibold text-foreground">
                  {payouts.count}
                </div>
              </div>

              {payouts.best && (
                <div>
                  <div className="text-xs text-muted-foreground">{t.reports.payoutsBest}</div>
                  <div className="text-xl font-mono font-semibold text-foreground">
                    {formatCurrency(payouts.best.amount, metrics.currency)}
                  </div>
                  {payouts.best.propFirmName && (
                    <div className="text-xs text-muted-foreground mt-0.5 truncate">
                      {payouts.best.propFirmName}
                    </div>
                  )}
                </div>
              )}
            </div>

            {payouts.byPropFirm.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {payouts.byPropFirm.map((firm, i) => (
                      <tr key={`${firm.propFirmName ?? ""}-${i}`} className="border-b border-border last:border-0">
                        <td className="py-2 pr-4 text-foreground truncate">
                          {firm.propFirmName ?? t.reports.payoutsNoPropFirm}
                        </td>
                        <td className="py-2 pr-4 text-right font-mono text-muted-foreground">
                          {firm.count}
                        </td>
                        <td className="py-2 text-right font-mono text-foreground">
                          {formatCurrency(firm.total, metrics.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
